import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  TextField,
} from "@mui/material";
import { FilterList, ArrowBack, Edit, Delete, Check, Close } from "@mui/icons-material";
import { themes, tableHeaderSx } from "services/constants";
import {
  CustomButton,
  CustomButtonWithBorder,
} from "components/CustomComponents/CustomButton";
import Papa from "papaparse";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import sessionHandling from "services/utils/notificationUtils";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const mapHeaderIndex = (headers, aliases) => {
  const normalized = headers.map((header) =>
    String(header || "")
      .trim()
      .toLowerCase(),
  );
  for (const alias of aliases) {
    const index = normalized.indexOf(alias.toLowerCase());
    if (index !== -1) {
      return index;
    }
  }
  return -1;
};

const duplicateCodeMessage = "Duplicate employee code";
const amountCannotBeZeroMessage = "Amount cannot be zero";

const getDuplicateDetails = (rows) => {
  const rowsByCode = new Map();

  rows.forEach((row) => {
    const code = String(row.code || "").trim();
    const normalizedCode = code.toLowerCase();

    if (!code || code === "-") return;

    if (!rowsByCode.has(normalizedCode)) {
      rowsByCode.set(normalizedCode, {
        code,
        rows: [],
      });
    }

    rowsByCode.get(normalizedCode).rows.push(row.rowNumber);
  });

  return Array.from(rowsByCode.values())
    .filter((duplicate) => duplicate.rows.length > 1)
    .map((duplicate) => ({
      ...duplicate,
      rows: duplicate.rows.sort((firstRow, secondRow) => firstRow - secondRow),
    }));
};

const applyDuplicateCodeErrors = (rows) => {
  const duplicates = getDuplicateDetails(rows);
  const duplicateCodes = new Set(
    duplicates.map((duplicate) => duplicate.code.toLowerCase()),
  );

  const updatedRows = rows.map((row) => {
    const errors = (row.errors || []).filter(
      (error) => error !== duplicateCodeMessage,
    );
    const normalizedCode = String(row.code || "").trim().toLowerCase();

    if (duplicateCodes.has(normalizedCode)) {
      errors.push(duplicateCodeMessage);
    }

    return {
      ...row,
      errors,
      status: errors.length > 0 ? "error" : "valid",
    };
  });

  return {
    rows: updatedRows,
    duplicates,
  };
};

export const BulkImportPreview = ({ data, onNext, onBack, onUpdate, payScheduleId }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [rows, setRows] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [isValidating, setIsValidating] = useState(false);
  const [parseError, setParseError] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");
  const [duplicates, setDuplicates] = useState([]);

  const parseFile = useCallback(
    async (file) => {
      setIsValidating(true);
      setParseError("");
      setDuplicates([]);

      Papa.parse(file, {
        complete: async (results) => {
          const parsed = results.data || [];
          const rawHeaders = parsed[0]
            ? parsed[0].map((cell) => String(cell || "").trim())
            : [];

          const idIndex = mapHeaderIndex(rawHeaders, [
            "Employee ID",
            "Code",
            "Employee Code",
          ]);
          const nameIndex = mapHeaderIndex(rawHeaders, [
            "Employee Name",
            "Name",
          ]);
          const amountIndex = mapHeaderIndex(rawHeaders, [
            "Amount",
            "Salary Amount",
            "Value",
          ]);

          const processedRows = parsed
            .slice(1)
            .filter((row) =>
              row.some((cell) => String(cell || "").trim() !== ""),
            )
            .map((row, index) => {
              const code = String(row[idIndex] ?? row[0] ?? "").trim();
              const name = String(row[nameIndex] ?? row[1] ?? "").trim();
              const rawAmount = String(row[amountIndex] ?? row[2] ?? "").trim();
              const amountValue = Number(rawAmount.replace(/[^0-9.-]+/g, ""));
              const amount = Number.isFinite(amountValue) ? amountValue : null;
              const amountLabel =
                amount != null
                  ? currencyFormatter.format(amount)
                  : rawAmount || "-";

              const errors = [];
              if (!code) errors.push("Code is missing");
              if (!name) errors.push("Employee name is missing");
              if (amount == null || amount === 0)
                errors.push(amountCannotBeZeroMessage);

              return {
                rowNumber: index + 1,
                code: code || "-",
                name: name || "Missing",
                amountLabel,
                hasAmountError: amount == null || amount === 0,
                errors,
                status: errors.length > 0 ? "error" : "valid",
              };
            });

          const {
            rows: rowsWithDuplicateErrors,
            duplicates: foundDuplicates,
          } = applyDuplicateCodeErrors(processedRows);
          setDuplicates(foundDuplicates);

          // Call validation API for employee codes and names
          try {
            const employeesToValidate = rowsWithDuplicateErrors.map((row) => {
              const rawAmount = String(row.amountLabel || "").replace(/[^0-9.-]+/g, "");
              const amountValue = Number(rawAmount);
              return {
                code: row.code,
                name: row.name,
                amount: Number.isFinite(amountValue) ? amountValue : 0,
              };
            });

            const validationResponse = await fetch(
              `${domain}payroll/operations/payrollprocessing/payrollruncycle/validate_payroll_data/?company_id=${requestCompanyId}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `token ${token}`,
                },
                body: JSON.stringify({
                  payroll_id: payScheduleId,
                  employees: employeesToValidate,
                }),
              },
            );

            if (validationResponse.ok) {
              const validationResult = await validationResponse.json();

              // Update rows with API validation results
              const updatedRows = rowsWithDuplicateErrors.map((row, index) => {
                const apiValidation = validationResult.employees?.[index] ?? validationResult?.[index];
                const apiErrors = [];

                if (
                  apiValidation &&
                  (apiValidation.error || apiValidation.validate === "error")
                ) {
                  apiErrors.push(apiValidation.reason);
                }

                const allErrors = [...row.errors, ...apiErrors];

                return {
                  ...row,
                  errors: allErrors,
                  status: allErrors.length > 0 ? "error" : "valid",
                };
              });

              setRows(updatedRows);
              // Pass updated preview data back to parent
              if (onUpdate) {
                onUpdate({ previewData: updatedRows });
              }
            } else if (validationResponse.status === 409) {
              sessionHandling();
            } else {
              // If API fails, proceed with local validation only
              setRows(rowsWithDuplicateErrors);
              if (onUpdate) {
                onUpdate({ previewData: rowsWithDuplicateErrors });
              }
              showToast(
                "warning",
                "Could not validate employee data with server. Using local validation only.",
              );
            }
          } catch (error) {
            // If API call fails, proceed with local validation only
            setRows(rowsWithDuplicateErrors);
            if (onUpdate) {
              onUpdate({ previewData: rowsWithDuplicateErrors });
            }
            console.error("Validation API error:", error);
            showToast(
              "warning",
              "Could not validate employee data with server. Using local validation only.",
            );
          }

          setIsValidating(false);
        },
        error: (error) => {
          setParseError("Unable to parse CSV: " + error.message);
          setIsValidating(false);
        },
      });
    },
    [domain, payScheduleId, token, onUpdate, requestCompanyId],
  );

  useEffect(() => {
    if (data?.csvFile) {
      parseFile(data.csvFile);
    }
  }, [data?.csvFile, parseFile]);

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) => selectedFilter === "all" || row.status === selectedFilter,
      ),
    [rows, selectedFilter],
  );

  const totals = useMemo(
    () => ({
      totalRows: rows.length,
      validRows: rows.filter((row) => row.status === "valid").length,
      errorRows: rows.filter((row) => row.status === "error").length,
    }),
    [rows],
  );

  const canProceed =
    totals.errorRows === 0 &&
    totals.totalRows > 0 &&
    !rows.some((row) => row.isEdited);

  const handleEdit = (rowNumber) => {
    const row = rows.find((r) => r.rowNumber === rowNumber);
    if (row) {
      const rawAmount = String(row.amountLabel || "").replace(/[^0-9.-]+/g, "");
      setEditingRow(rowNumber);
      setEditCode(row.code);
      setEditName(row.name);
      setEditAmount(rawAmount === "0" ? "" : rawAmount);
    }
  };

  const handleSaveRow = (rowNumber) => {
    const code = editCode.trim();
    const name = editName.trim();
    const rawAmount = editAmount.trim();
    const amountValue = Number(rawAmount.replace(/[^0-9.-]+/g, ""));
    const amount = Number.isFinite(amountValue) ? amountValue : null;
    const amountLabel =
      amount != null ? currencyFormatter.format(amount) : rawAmount || "-";

    const locallyValidatedRows = rows.map((row) => {
      if (row.rowNumber !== rowNumber) return row;

      const errors = [
        ...(!code ? ["Code is missing"] : []),
        ...(!name ? ["Employee name is missing"] : []),
        ...(amount == null || amount === 0 ? [amountCannotBeZeroMessage] : []),
      ];

      return {
        ...row,
        code: code || "-",
        name: name || "Missing",
        amountLabel,
        hasAmountError: amount == null || amount === 0,
        errors,
        status: errors.length > 0 ? "error" : "valid",
        isEdited: true,
      };
    });
    const { rows: updatedRows, duplicates: foundDuplicates } =
      applyDuplicateCodeErrors(locallyValidatedRows);
    
    setRows(updatedRows);
    setDuplicates(foundDuplicates);
    // Pass updated preview data back to parent
    if (onUpdate) {
      onUpdate({ previewData: updatedRows });
    }
    
    setEditingRow(null);
    setEditCode("");
    setEditName("");
    setEditAmount("");
  };

  const handleResubmitForReview = async () => {
    setIsValidating(true);
    
    try {
      const employeesToValidate = rows.map((row) => {
        const rawAmount = String(row.amountLabel || "").replace(/[^0-9.-]+/g, "");
        const amountValue = Number(rawAmount);
        return {
          code: row.code,
          name: row.name,
          amount: Number.isFinite(amountValue) ? amountValue : 0,
        };
      });

      const validationResponse = await fetch(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/validate_payroll_data/?company_id=${requestCompanyId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({
            payroll_id: payScheduleId,
            employees: employeesToValidate,
          }),
        },
      );

      if (validationResponse.ok) {
        const validationResult = await validationResponse.json();

        // Update rows with API validation results
        const locallyValidatedRows = rows.map((row, index) => {
          const apiValidation = validationResult.employees?.[index] ?? validationResult?.[index];
          const apiErrors = [];

          if (
            apiValidation &&
            (apiValidation.error || apiValidation.validate === "error")
          ) {
            apiErrors.push(apiValidation.reason);
          }

          // Keep local validation errors and add API errors
          const localErrors = [];
          if (!row.code) localErrors.push("Code is missing");
          if (!row.name) localErrors.push("Employee name is missing");
          const rawAmount = String(row.amountLabel || "").replace(/[^0-9.-]+/g, "");
          const amountValue = Number(rawAmount);
          if (!Number.isFinite(amountValue) || amountValue === 0) localErrors.push(amountCannotBeZeroMessage);

          const allErrors = [...localErrors, ...apiErrors];

          return {
            ...row,
            errors: allErrors,
            status: allErrors.length > 0 ? "error" : "valid",
            isEdited: false,
          };
        });
        const { rows: updatedRows, duplicates: foundDuplicates } =
          applyDuplicateCodeErrors(locallyValidatedRows);

        setRows(updatedRows);
        setDuplicates(foundDuplicates);
        // Pass updated preview data back to parent
        if (onUpdate) {
          onUpdate({ previewData: updatedRows });
        }
        showToast("success", "Data re-validated.");
      } else if (validationResponse.status === 409) {
        sessionHandling();
      } else {
        showToast("warning", "Could not re-validate data with server. Local validation applied.");
      }
    } catch (error) {
      console.error("Resubmit validation error:", error);
      showToast("warning", "Could not re-validate data with server. Local validation applied.");
    }

    setIsValidating(false);
  };

  const handleDelete = (rowNumber) => {
    const { rows: updatedRows, duplicates: foundDuplicates } =
      applyDuplicateCodeErrors(
        rows.filter((row) => row.rowNumber !== rowNumber),
      );
    setRows(updatedRows);
    setDuplicates(foundDuplicates);
    // Pass updated preview data back to parent
    if (onUpdate) {
      onUpdate({ previewData: updatedRows });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Preview & import
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the data before confirming the import.
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={selectedFilter}
          exclusive
          onChange={(event, value) => value && setSelectedFilter(value)}
          sx={{
            bgcolor: themes?.miliedLightGray,
            borderRadius: 2,
            p: 0.5,
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: 1.5,
              textTransform: "none",
              px: 1.75,
              py: 0.75,
              color: themes?.grayColor,
              fontSize: "0.85rem",
            },
            "& .MuiToggleButton-root.Mui-selected": {
              bgcolor: themes?.whiteColor,
              color: themes?.stepActiveColor,
              boxShadow: "none",
            },
          }}
        >
          <ToggleButton value="all">All rows ({totals.totalRows})</ToggleButton>
          <ToggleButton value="valid">Valid ({totals.validRows})</ToggleButton>
          <ToggleButton value="error">Errors ({totals.errorRows})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total rows", value: totals.totalRows, color: "#1E88E5" },
          { label: "Valid rows", value: totals.validRows, color: "#00D4AA" },
          { label: "Errors", value: totals.errorRows, color: "#EF5350" },
        ].map((item) => (
          <Paper
            key={item.label}
            sx={{
              p: 2,
              flex: 1,
              minWidth: 140,
              borderRadius: 2,
              backgroundColor: themes?.whiteColor,
              border: `1px solid ${themes?.miliedLightGray}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: themes?.generalSubHeader }}
            >
              {item.label}
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mt: 1, color: item.color }}
            >
              {item.value}
            </Typography>
          </Paper>
        ))}
      </Stack>

      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${themes?.miliedLightGray}`,
          backgroundColor: themes?.whiteColor,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {data?.componentType?.label || "Earning"} ·{" "}
            {data?.componentName?.label || "Basic salary"}
          </Typography>
          <Chip
            icon={<FilterList />}
            label={
              selectedFilter === "all"
                ? `All rows (${totals.totalRows})`
                : selectedFilter === "valid"
                  ? `Valid rows (${totals.validRows})`
                  : `Error rows (${totals.errorRows})`
            }
            size="small"
            variant="outlined"
            sx={{
              borderColor: themes?.miliedLightGray,
              color: themes?.grayColor,
              fontWeight: 600,
            }}
          />
        </Box>

        {isValidating ? (
          <Stack sx={{ py: 6, alignItems: "center" }}>
            <Typography variant="body2">
              Validating employee data with server...
            </Typography>
          </Stack>
        ) : parseError ? (
          <Alert severity="error">{parseError}</Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader>
              <TableHead
                sx={{
                  ...tableHeaderSx,
                  backgroundColor: themes?.sidebarDivider,
                  position: "sticky",
                  top: 0,
                  zIndex: 11,
                }}
              >
                <TableRow>
                {["S.No", "Employee Id", "Employee name", "Amount", "Status", "Action"].map(
                    (column) => (
                      <TableCell
                        key={column}
                        sx={{
                          color: themes?.generalText || "#2D3748",
                          fontWeight: 700,
                          py: 1.5,
                          px: 1.5,
                          fontSize: "0.92rem",
                          borderBottom: `1px solid ${themes?.miliedLightGray}`,
                          backgroundColor: themes?.sidebarDivider,
                        }}
                      >
                        {column}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: row.isEdited
                          ? "rgba(255, 243, 205, 0.85)"
                          : row.status === "error"
                            ? "rgba(239,83,80,0.08)"
                            : row.status === "valid"
                              ? "rgba(0,212,170,0.07)"
                              : "transparent",
                        "&:hover": {
                          backgroundColor: row.isEdited
                            ? "rgba(255, 239, 179, 0.95)"
                            : row.status === "error"
                              ? "rgba(239,83,80,0.12)"
                              : "rgba(0,212,170,0.12)",
                        },
                      }}
                    >
                      <TableCell>{row.rowNumber}</TableCell>
                      <TableCell>
                        {editingRow === row.rowNumber ? (
                          <TextField
                            value={editCode}
                            onChange={(e) => setEditCode(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveRow(row.rowNumber);
                              if (e.key === "Escape") {
                                setEditingRow(null);
                                setEditCode("");
                                setEditName("");
                                setEditAmount("");
                              }
                            }}
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="Employee Code"
                          />
                        ) : (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: "rgba(255,255,255,0.08)",
                              fontSize: "0.85rem",
                              fontWeight: 600,
                            }}
                          >
                            {row.code}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRow === row.rowNumber ? (
                          <TextField
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveRow(row.rowNumber);
                              if (e.key === "Escape") {
                                setEditingRow(null);
                                setEditCode("");
                                setEditName("");
                                setEditAmount("");
                              }
                            }}
                            size="small"
                            fullWidth
                            placeholder="Employee Name"
                          />
                        ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name}
                      </Typography>
                    )}
                      </TableCell>
                      <TableCell>
                        {editingRow === row.rowNumber ? (
                          <TextField
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveRow(row.rowNumber);
                              if (e.key === "Escape") {
                                setEditingRow(null);
                                setEditCode("");
                                setEditName("");
                                setEditAmount("");
                              }
                            }}
                            size="small"
                            fullWidth
                            placeholder="Amount"
                          />
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.amountLabel}
                          </Typography>
                        )}
                  </TableCell>
                  <TableCell>
                    {!row.isEdited && (
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Chip
                          label={row.status === "valid" ? "Valid" : "Error"}
                          size="small"
                          color={row.status === "valid" ? "success" : "error"}
                          sx={{ height: 22 }}
                        />
                        {row.status === "error" && row.errors.map((error, idx) => (
                          <Typography
                            key={idx}
                            variant="caption"
                            color="#EF5350"
                            sx={{ display: "block", lineHeight: 1.2 }}
                          >
                            • {error}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {editingRow === row.rowNumber ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleSaveRow(row.rowNumber)}
                                color="primary"
                              >
                                <Check fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingRow(null);
                                  setEditCode("");
                                  setEditName("");
                                  setEditAmount("");
                                }}
                                color="error"
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(row.rowNumber)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(row.rowNumber)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No rows available to preview.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totals.errorRows > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {totals.errorRows} rows have errors. Please edit or remove them
            before proceeding.
          </Alert>
        )}

        {duplicates.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Duplicate employee codes detected:
            </Typography>
            {duplicates.map((dup, index) => (
              <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                • <strong>{dup.code}</strong> appears in rows {dup.rows.join(" and ")}
              </Typography>
            ))}
            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
              Please ensure each employee code is unique or manually correct the duplicates.
            </Typography>
          </Alert>
        )}
      </Paper>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mt: 3,
          flexWrap: "wrap",
        }}
      >
        <CustomButtonWithBorder
          actionName="Back"
          actionFuntion={onBack}
          customTitleSx={{ textTransform: "none" }}
          AddIcon={<ArrowBack fontSize="small" />}
        />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <CustomButton
            actionName="Resubmit for Review"
            actionFuntion={handleResubmitForReview}
            disableAction={isValidating}
            loading={isValidating}
            customTitleSx={{ textTransform: "none" }}
          />
          <CustomButton
            actionName="Proceed to Import"
            actionFuntion={onNext}
            disableAction={!canProceed}
            loading={false}
            customTitleSx={{ textTransform: "none" }}
          />
        </Box>
      </Box>
    </Box>
  );
};
